import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideTranslateService, TranslateLoader, TranslateService, TranslationObject } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { lastValueFrom } from 'rxjs';

import { appRoutes } from './app.routes';
import { API_URL } from './core/tokens/api-url.token';
import { SITE_URL } from './core/tokens/site-url.token';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { LanguageStore } from './core/stores/language.store';
import { ThemeStore } from './core/stores/theme.store';
import { AuthStore } from './core/stores/auth.store';
import { AuthService } from './core/services/auth.service';
import { environment } from '../environments/environment';

import he from '../assets/i18n/he.json';
import en from '../assets/i18n/en.json';

const translations: Record<string, TranslationObject> = { he, en };

class StaticTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<TranslationObject> {
    return of(translations[lang] ?? translations['he']);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideClientHydration(withEventReplay()),
    provideRouter(appRoutes, withComponentInputBinding(), withViewTransitions(), withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideTranslateService({
      fallbackLang: 'he',
      loader: { provide: TranslateLoader, useClass: StaticTranslateLoader },
    }),
    { provide: API_URL, useValue: environment.apiUrl },
    { provide: SITE_URL, useValue: environment.siteUrl },
    provideAppInitializer(async () => {
      const langStore = inject(LanguageStore);
      inject(ThemeStore);
      const translate = inject(TranslateService);
      const authStore = inject(AuthStore);
      const authService = inject(AuthService);

      translate.addLangs(['he', 'en']);
      await lastValueFrom(translate.use(langStore.language()));

      if (authStore.token()) {
        authService.fillUserData().subscribe();
      }
    }),
  ],
};
